import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import { Briefcase, Plus, Trash2, Edit, Save, X, AlertCircle, CheckCircle } from "lucide-react";

const API_URL = process.env.REACT_APP_API_URL;

const PuestosModule = () => {
  const { getStoredToken } = useAuth();
  const [puestos, setPuestos] = useState([]);
  const [nuevoPuesto, setNuevoPuesto] = useState("");
  const [nuevaDescripcion, setNuevaDescripcion] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  // Edicion en linea (agregar/corregir la descripcion de un puesto ya existente)
  const [editandoId, setEditandoId] = useState(null);
  const [editForm, setEditForm] = useState({ nombre: "", descripcion: "" });

  const fetchPuestos = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const token = getStoredToken();
      const res = await axios.get(`${API_URL}/puestos`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('📊 Datos recibidos de puestos:', res.data);
      
      // ✅ VALIDAR FORMATO DE RESPUESTA
      if (res.data.puestos && Array.isArray(res.data.puestos)) {
        setPuestos(res.data.puestos);
      } else if (Array.isArray(res.data)) {
        setPuestos(res.data);
      } else {
        console.error('Formato inesperado:', res.data);
        setPuestos([]);
        setError('Formato de datos inesperado');
      }
      
    } catch (err) {
      console.error('Error cargando puestos:', err);
      setError(err.response?.data?.message || 'Error cargando puestos');
      setPuestos([]);
    } finally {
      setLoading(false);
    }
  };

  const agregarPuesto = async () => {
    if (!nuevoPuesto.trim()) {
      setError('El nombre del puesto es requerido');
      return;
    }
    
    setLoading(true);
    setError(null);
    setMessage(null);
    
    try {
      const token = getStoredToken();
      await axios.post(
        `${API_URL}/puestos`,
        { nombre: nuevoPuesto.trim(), descripcion: nuevaDescripcion.trim() || null },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setNuevoPuesto("");
      setNuevaDescripcion("");
      setMessage('Puesto agregado exitosamente');
      await fetchPuestos();
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setMessage(null), 3000);
      
    } catch (err) {
      console.error('Error agregando puesto:', err);
      setError(err.response?.data?.message || 'Error agregando puesto');
    } finally {
      setLoading(false);
    }
  };

  const eliminarPuesto = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este puesto?')) {
      return;
    }
    
    setLoading(true);
    setError(null);
    setMessage(null);
    
    try {
      const token = getStoredToken();
      await axios.delete(`${API_URL}/puestos/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      setMessage('Puesto eliminado exitosamente');
      await fetchPuestos();
      
      // Limpiar mensaje después de 3 segundos
      setTimeout(() => setMessage(null), 3000);
      
    } catch (err) {
      console.error('Error eliminando puesto:', err);
      setError(err.response?.data?.message || 'Error eliminando puesto');
    } finally {
      setLoading(false);
    }
  };

  const iniciarEdicion = (puesto) => {
    setEditandoId(puesto.id || puesto.PuestoID || puesto.PUESTO_ID);
    setEditForm({
      nombre: puesto.nombre || puesto.Nombre || puesto.NOMBRE || '',
      descripcion: puesto.descripcion || puesto.Descripcion || '',
      // Se conservan tal cual: updatePuesto sobrescribe estos campos en vez de dejarlos
      // igual si no se envian, asi que hay que reenviarlos para no borrarlos.
      salarioMinimo: puesto.salarioMinimo ?? puesto.SalarioMinimo ?? null,
      salarioMaximo: puesto.salarioMaximo ?? puesto.SalarioMaximo ?? null,
      departamentoID: puesto.departamentoID ?? puesto.DepartamentoID ?? null,
    });
  };

  const cancelarEdicion = () => {
    setEditandoId(null);
    setEditForm({ nombre: "", descripcion: "" });
  };

  const guardarEdicion = async () => {
    if (!editForm.nombre.trim()) {
      setError('El nombre del puesto es requerido');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    try {
      const token = getStoredToken();
      await axios.put(
        `${API_URL}/puestos/${editandoId}`,
        {
          nombre: editForm.nombre.trim(),
          descripcion: editForm.descripcion.trim() || null,
          salarioMinimo: editForm.salarioMinimo,
          salarioMaximo: editForm.salarioMaximo,
          departamentoID: editForm.departamentoID,
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setMessage('Puesto actualizado exitosamente');
      cancelarEdicion();
      await fetchPuestos();

      setTimeout(() => setMessage(null), 3000);

    } catch (err) {
      console.error('Error actualizando puesto:', err);
      setError(err.response?.data?.message || 'Error actualizando puesto');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      agregarPuesto();
    }
  };

  useEffect(() => {
    fetchPuestos();
  }, []);

  // Limpiar mensajes después de 5 segundos
  useEffect(() => {
    if (error || message) {
      const timer = setTimeout(() => {
        setError(null);
        setMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, message]);

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="w-full">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm mb-6 p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Briefcase className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Gestión de Puestos</h2>
          </div>
          <p className="text-gray-600 text-sm">
            Administra los puestos de trabajo disponibles en la organización
          </p>
        </div>

        {/* Mensajes de estado */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-800">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {message && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-800">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span>{message}</span>
          </div>
        )}

        {/* Formulario para agregar puesto */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Agregar Nuevo Puesto</h3>
          <div className="flex flex-col gap-3">
            <input
              type="text"
              value={nuevoPuesto}
              onChange={(e) => setNuevoPuesto(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Nombre del puesto (ej: Desarrollador Senior)"
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={loading}
            />
            <textarea
              value={nuevaDescripcion}
              onChange={(e) => setNuevaDescripcion(e.target.value)}
              placeholder="Descripcion del puesto (funciones, responsabilidades, requisitos...) - opcional"
              rows={3}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-y"
              disabled={loading}
            />
            <button
              onClick={agregarPuesto}
              disabled={loading || !nuevoPuesto.trim()}
              className="self-start px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
            >
              <Plus className="w-5 h-5" />
              {loading ? 'Agregando...' : 'Agregar'}
            </button>
          </div>
        </div>

        {/* Lista de puestos */}
        <div className="bg-white rounded-lg shadow-sm">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              Puestos Registrados ({Array.isArray(puestos) ? puestos.length : 0})
            </h3>
          </div>

          {loading && puestos.length === 0 ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Cargando puestos...</p>
            </div>
          ) : Array.isArray(puestos) && puestos.length === 0 ? (
            <div className="p-8 text-center">
              <Briefcase className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">No hay puestos registrados</p>
              <p className="text-gray-500 text-sm mt-1">Agrega el primer puesto usando el formulario de arriba</p>
            </div>
          ) : (
            <ul className="divide-y divide-gray-200">
              {Array.isArray(puestos) && puestos.map((puesto) => {
                const id = puesto.id || puesto.PuestoID || puesto.PUESTO_ID;
                const descripcionActual = puesto.descripcion || puesto.Descripcion || '';

                if (editandoId === id) {
                  return (
                    <li key={id} className="px-6 py-4 bg-blue-50">
                      <div className="flex flex-col gap-2">
                        <input
                          type="text"
                          value={editForm.nombre}
                          onChange={(e) => setEditForm(f => ({ ...f, nombre: e.target.value }))}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={loading}
                        />
                        <textarea
                          value={editForm.descripcion}
                          onChange={(e) => setEditForm(f => ({ ...f, descripcion: e.target.value }))}
                          placeholder="Descripcion del puesto (funciones, responsabilidades, requisitos...) - opcional"
                          rows={3}
                          className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                          disabled={loading}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={guardarEdicion}
                            disabled={loading || !editForm.nombre.trim()}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                          >
                            <Save className="w-4 h-4" />
                            Guardar
                          </button>
                          <button
                            onClick={cancelarEdicion}
                            disabled={loading}
                            className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 font-medium"
                          >
                            <X className="w-4 h-4" />
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </li>
                  );
                }

                return (
                  <li
                    key={id}
                    className="px-6 py-4 hover:bg-gray-50 transition-colors flex items-start justify-between gap-4"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="p-2 bg-gray-100 rounded-lg flex-shrink-0">
                        <Briefcase className="w-5 h-5 text-gray-600" />
                      </div>
                      <div className="min-w-0">
                        <span className="text-gray-900 font-medium block">
                          {puesto.nombre || puesto.Nombre || puesto.NOMBRE || 'Sin nombre'}
                        </span>
                        <p className={`text-sm mt-1 ${descripcionActual ? 'text-gray-600' : 'text-gray-400 italic'}`}>
                          {descripcionActual || 'Sin descripcion todavia'}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => iniciarEdicion(puesto)}
                        disabled={loading}
                        className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                      >
                        <Edit className="w-4 h-4" />
                        Editar
                      </button>
                      <button
                        onClick={() => eliminarPuesto(id)}
                        disabled={loading}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 font-medium"
                      >
                        <Trash2 className="w-4 h-4" />
                        Eliminar
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default PuestosModule;
